import { Action } from '@reduxjs/toolkit';
import { addDays, isAfter } from 'date-fns';
import { combineLatest, EMPTY, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { createSaveTableSettingsEpic } from '../../../common/datagrid';
import {
    notifyExternalPriceUpdated,
    notifyPriceCommentUpdated
} from '../../../common/signalr/signalrActions';
import { CommentType } from '../../../common/signalr/signalrTypes';
import { RootEpic } from '../../../store';
import { createEpicFromAsyncAction } from '../../../utils';
import { fetchExternalPricelists } from '../../externalpricelist/redux/externalPricelistSlices';
import { fetchPricelists } from '../../pricelist/redux/pricelistSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { enqueueSnackbar, pricePrgError } from '../../ui/redux/uiSlices';
import {
    cancelExternalPriceUpdate,
    externalPricelistDateSelected,
    externalPriceTableActions,
    externalPriceTablePricelistsSelected,
    fetchExternalPricelistPeriods,
    fetchExternalPrices,
    fetchExternalPricesByIds,
    fetchExternalPriceSource,
    fetchExternalPriceSourcesPartial,
    fetchExternalPricesPartial,
    pricelistColumnsSelected,
    removeExternalPrices,
    removeExternalPricesSources,
    saveExternalPriceComment,
    saveExternalPriceDiscount,
    updateExternalPrice
} from './externalPriceSlices';

const fetchExternalPrices$ = createEpicFromAsyncAction(
    fetchExternalPrices,
    (payload, { externalPriceClient }) =>
        externalPriceClient.getPricesByPricelistAndDate(payload.date, payload.pricelistIds),
    (req, resp) => fetchExternalPriceSource.request(resp.map(c => c.priceId))
);

const fetchExternalPriceSource$ = createEpicFromAsyncAction(
    fetchExternalPriceSource,
    (payload, { priceClient }) => priceClient.getPricesByIds(payload)
);

const fetchExternalPricelistPeriods$ = createEpicFromAsyncAction(
    fetchExternalPricelistPeriods,
    (payload, { externalPricelistClient }) =>
        externalPricelistClient.getPricelistPeriodsPOST(payload.extPricelistIds).then(result => ({
            preselectDate: payload.preselectDate,
            pricelistPeriods: result
        }))
);

const updateExternalPrice$ = createEpicFromAsyncAction(
    updateExternalPrice,
    (payload, { externalPriceClient }) => externalPriceClient.update(payload.extPriceId, payload.newPrice),
    req => notifyExternalPriceUpdated(req.extPriceId),
    req => [
        cancelExternalPriceUpdate(req.extPriceId),
        enqueueSnackbar({
            message: 'Failed to update price',
            severity: 'error',
            autoHideDuration: 15000
        })
    ]
);

const externalPricelistDateSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(externalPricelistDateSelected.match),
        withLatestFrom(state$),
        map(([action, state]) =>
            fetchExternalPrices.request({
                date: action.payload,
                pricelistIds: state.externalPrice.selectedExternalPricelistIds
            })
        )
    );

const externalPricelistSelected$: RootEpic = action$ =>
    action$.pipe(
        filter(externalPriceTablePricelistsSelected.match),
        map(action =>
            fetchExternalPricelistPeriods.request({
                extPricelistIds: action.payload,
                preselectDate: true
            })
        )
    );

const externalPricelistMappedToInPricelist$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(externalPriceTablePricelistsSelected.match),
        withLatestFrom(
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.externalPricelist.externalPricelists))
        ),
        map(([action, , externalPricelists]) => {
            const result = action.payload
                .map(externalPricelistId => {
                    const externalPricelist = externalPricelists[externalPricelistId];
                    if (externalPricelist == null) {
                        return null;
                    }
                    const inPricelistId = externalPricelist.pricelistId;
                    return { externalPricelistId: externalPricelist.externalPricelistId, inPricelistId };
                })
                .reduce(
                    (res, idObject) => {
                        if (idObject == null) {
                            return res;
                        }
                        res.externalPriceColumnIds.add(idObject.externalPricelistId);
                        if (idObject.inPricelistId != null) {
                            if (res.inPriceColumnIds[idObject.inPricelistId] == null) {
                                res.inPriceColumnIds[idObject.inPricelistId] = [];
                            }
                            res.inPriceColumnIds[idObject.inPricelistId.toString()].push(
                                idObject.externalPricelistId
                            );
                        }

                        return res;
                    },
                    {
                        inPriceColumnIds: {} as { [inPricelistId: number]: number[] },
                        externalPriceColumnIds: new Set<number>()
                    }
                );
            return pricelistColumnsSelected({
                inPriceColumnIds: result.inPriceColumnIds,
                externalPriceColumnIds: Array.from(result.externalPriceColumnIds)
            });
        })
    );

const saveExternalPriceComment$ = createEpicFromAsyncAction(
    saveExternalPriceComment,
    (req, { externalPriceClient }) =>
        externalPriceClient.updateExternalPriceComment(req.extPriceId, req.comment ?? '').then(() => ({
            comment: req.comment,
            extPriceIds: [req.extPriceId]
        })),
    req =>
        notifyPriceCommentUpdated({
            comment: req.comment,
            priceIds: [req.extPriceId],
            commentType: CommentType.ExtPrice,
            pricelistId: 0, // Not required for ext prices
            productId: 0 // Not required for ext prices
        })
);

const saveExternalPriceDiscount$ = createEpicFromAsyncAction(
    saveExternalPriceDiscount,
    (req, { externalPriceClient }) =>
        externalPriceClient.updateExternalPriceDiscount(req.extPriceId, req.discount ?? 0).then(() => req),
    req => notifyExternalPriceUpdated(req.extPriceId)
);

const saveExternalPriceTableSettings$ = createSaveTableSettingsEpic(
    externalPriceTableActions,
    state => state.externalPrice.ui.externalPriceTable,
    [pricelistColumnsSelected]
);

const extPriceTableSettingsLoadedFetchExtPricelistPeriods$: RootEpic = (action$, state$) =>
    // Use combine latest in order to not proceed with selecting columns until
    // ext pricelists and pricelists has been fetched.
    combineLatest([
        action$.pipe(filter(fetchUserSettings.success.match)),
        action$.pipe(filter(fetchPricelists.success.match), take(1)),
        action$.pipe(filter(fetchExternalPricelists.success.match), take(1))
    ]).pipe(
        withLatestFrom(state$.pipe(map(state => state.externalPrice.selectedExternalPricelistIds))),
        filter(([, selectedExtPricelistIds]) => selectedExtPricelistIds.length > 0),
        map(([, selectedExtPricelistIds]) => externalPriceTablePricelistsSelected(selectedExtPricelistIds))
    );

const setDefaultDateWhenExtPricelistsHasBeenFetched$: RootEpic = action$ =>
    action$.pipe(
        filter(fetchExternalPricelistPeriods.success.match),
        filter(a => a.payload.preselectDate),
        mergeMap(action => {
            const now = addDays(new Date(), 1);

            const dates = action.payload.pricelistPeriods
                .map(c => new Date(c.validFrom))
                .sort((a, b) => a.getTime() - b.getTime());

            // Gets the first date after today. If no such date exists
            // it will take the latest date.
            const selectedDate = dates.find(d => isAfter(d, now)) ?? dates[dates.length - 1];

            if (selectedDate == null) {
                return EMPTY;
            }

            return of(externalPricelistDateSelected(selectedDate.toISOString()));
        })
    );

const fetchExternalPricesByIds$: RootEpic = (action$, state$, { externalPriceClient }) =>
    action$.pipe(
        filter(fetchExternalPricesByIds.request.match),
        withLatestFrom(
            state$.pipe(map(state => state.externalPrice.externalPrices)),
            state$.pipe(map(state => state.externalPrice.selectedExternalPriceDate))
        ),
        map(([action, extPrices, date]) => {
            // Only refetch prices that we currently have in memory
            const currentExtPriceIds = new Set(
                Object.values(extPrices).map(extPrice => extPrice.externalPriceId)
            );

            return [action.payload.filter(extPriceId => currentExtPriceIds.has(extPriceId)), date] as const;
        }),
        filter(([extPriceIds, date]) => extPriceIds.length > 0 && date != null),
        mergeMap(([extPriceIds, date]) =>
            from(
                externalPriceClient.getExternalPricesByIds({
                    externalPriceIds: extPriceIds,
                    validFrom: date!
                })
            ).pipe(
                map(fetchExternalPricesByIds.success),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const fetchExternalPriceSourcesPartial$: RootEpic = (action$, state$, { priceClient }) =>
    action$.pipe(
        filter(fetchExternalPriceSourcesPartial.request.match),
        withLatestFrom(state$.pipe(map(state => state.externalPricelist.externalPricelists))),
        mergeMap(([a, extPls]) =>
            from(priceClient.getPricesByExternalPricelistIds(a.payload)).pipe(
                mergeMap(prices => {
                    const actions: Action[] = [fetchExternalPriceSourcesPartial.success(prices)];

                    const { extPricelistIds, productId } = a.payload;

                    // If this is true a price may have been removed
                    if (prices.length < extPricelistIds.length) {
                        const removedPrices = extPricelistIds
                            .map(extPlId => [extPlId, extPls[extPlId]?.pricelistId] as const)
                            .filter(
                                ([, plId]) =>
                                    plId != null &&
                                    prices.every(p => p.productId !== productId && p.pricelistId !== plId)
                            )
                            .map(([extPlId, plId]) => ({
                                productId: productId,
                                pricelistId: plId!,
                                externalPricelistId: extPlId
                            }));

                        if (removedPrices.length > 0) {
                            actions.push(removeExternalPricesSources(removedPrices));
                            actions.push(removeExternalPrices(removedPrices));
                        }
                    }

                    return from(actions);
                }),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const fetchUpdatedExternalPrices$ = createEpicFromAsyncAction(
    fetchExternalPricesPartial,
    (payload, { externalPriceClient }) => externalPriceClient.getExternalPricesPartial(payload)
);

export default [
    fetchExternalPrices$,
    fetchExternalPricelistPeriods$,
    externalPricelistSelected$,
    externalPricelistDateSelected$,
    updateExternalPrice$,
    fetchExternalPriceSource$,
    externalPricelistMappedToInPricelist$,
    saveExternalPriceComment$,
    saveExternalPriceDiscount$,
    saveExternalPriceTableSettings$,
    extPriceTableSettingsLoadedFetchExtPricelistPeriods$,
    fetchExternalPricesByIds$,
    setDefaultDateWhenExtPricelistsHasBeenFetched$,
    fetchExternalPriceSourcesPartial$,
    fetchUpdatedExternalPrices$
];
